import Link from 'next/link'
import Image from 'next/image'
import {
	Alert,
	Badge,
	Image as MantineImage,
	Paper,
	Popover,
	Spoiler,
	Text,
	Title,
	Center,
	Pagination,
	Box,
} from '@mantine/core'
import {
	IconCertificate,
	IconId,
	IconWorldWww,
	IconStar,
	IconCrown,
} from '@tabler/icons-react'
import classNames from 'classnames'
import pluralize from 'pluralize'
import { useRouter } from 'next/router'
import { useRef, useState, useEffect, MouseEvent } from 'react'
import * as Types from 'declarations'
import { getTrekRoute, getUploadThumbPath } from 'lib/display'
import { sluggify } from 'lib/helper'
import CountryBadge from 'components/CountryBadge'

// Reads the current page from the `?page=` query param so paginated guide
// listings are server-rendered and crawlable (each page has a real URL with
// real <a href> links to every other page).
const parsePageParam = (raw: string | string[] | undefined) => {
	const value = Array.isArray(raw) ? raw[0] : raw
	const parsed = Number.parseInt(value ?? '', 10)
	return Number.isNaN(parsed) ? 1 : parsed
}

const GuideDirectoryList = ({
	guides,
	highlightedCountryISOCodes,
	highlightedTripIds,
}: {
	guides: Types.API.GuideCompanyOverview[]
	highlightedCountryISOCodes: string[]
	highlightedTripIds: number[]
}) => {
	const router = useRouter()
	const [activePage, setActivePage] = useState(() =>
		parsePageParam(router.query.page),
	)
	const ITEMS_PER_PAGE = 20
	const listRef = useRef<HTMLDivElement>(null)

	const totalPages = Math.max(Math.ceil(guides.length / ITEMS_PER_PAGE), 1)
	const currentPage = Math.min(Math.max(activePage, 1), totalPages)
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
	const visibleGuides = guides.slice(startIndex, startIndex + ITEMS_PER_PAGE)

	// Dynamic route params live in the path, not the query string — keep them
	// out of generated pagination URLs (e.g. on /climbing/[continent]/[country]/guides).
	const routeParamKeys = (
		router.pathname.match(/\[([^\[\]]+)\]/g) ?? []
	).map((segment) => segment.slice(1, -1))

	const buildPageUrl = (page: number) => {
		const params = new URLSearchParams()
		Object.entries(router.query).forEach(([key, value]) => {
			if (key === 'page' || routeParamKeys.includes(key)) return
			if (value === undefined || value === null) return
			const values = Array.isArray(value) ? value : [value]
			values.forEach((entry) => params.append(key, String(entry)))
		})
		if (page > 1) params.set('page', String(page))
		const queryString = params.toString()
		const path = router.asPath.split('?')[0].split('#')[0]
		return `${path}${queryString ? `?${queryString}` : ''}`
	}

	const handlePageChange = (newPage: number) => {
		if (newPage < 1 || newPage > totalPages) return
		setActivePage(newPage)
		// Shallow: keeps the instant client-side pagination UX while giving
		// every page a shareable/crawlable URL.
		router.push(buildPageUrl(newPage), undefined, { shallow: true })
		listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}

	// Stay in sync with the ?page= query param (deep links, back/forward nav,
	// filters resetting the page to 1).
	useEffect(() => {
		setActivePage(parsePageParam(router.query.page))
	}, [router.query.page])

	// Intercept plain left-clicks for instant shallow navigation; modified
	// clicks (new tab/window) fall through to the native anchor behaviour.
	const handleAnchorClick = (page: number) => (event: MouseEvent<any>) => {
		if (event.defaultPrevented) return
		if (event.button !== 0) return
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
		event.preventDefault()
		handlePageChange(page)
	}

	const pageAnchorProps = (page: number) => ({
		component: 'a',
		href: buildPageUrl(page),
		onClick: handleAnchorClick(page),
	})

	return (
		<Box>
			<div ref={listRef} className="guide-directory-list">
				{visibleGuides.map((guide, key) => (
					<div key={'guide' + key}>
						<Paper
							shadow={guide.isPremium ? 'lg' : 'md'}
							radius="md"
							p="md"
							withBorder
							mb="xl"
							style={{
								borderColor: guide.isPremium
									? '#B8860B'
									: 'var(--mantine-color-gray-3)',
								borderWidth: guide.isPremium ? '2px' : '1px',
								position: 'relative',
								overflow: 'hidden',
								...(guide.isPremium && {
									background:
										'linear-gradient(to bottom, rgba(255, 240, 180, 0.15), rgba(255, 255, 255, 1))',
								}),
							}}
						>
							{guide.isPremium && (
								<div
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '6px',
										background:
											'linear-gradient(90deg, #B8860B, #FFD700, #B8860B)',
									}}
								/>
							)}
							<div className="title-flags-logo">
								{!!guide.jsonProfile.logoUploadId && (
									<div
										className="logo"
										style={
											guide.isPremium
												? {
														padding: '10px',
														background: 'white',
														borderRadius: '8px',
														boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
												  }
												: undefined
										}
									>
										<Image
											src={getUploadThumbPath(
												guide.jsonProfile.logoUploadId,
												'medium',
											)}
											alt={`${guide.companyName} logo`}
											width={guide.isPremium ? 100 : 80}
											height={guide.isPremium ? 100 : 80}
											style={{
												borderRadius: 8,
												objectFit: 'contain',
												...(guide.isPremium
													? {
															filter:
																'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
													  }
													: {}),
											}}
										/>
									</div>
								)}
								<div className="title-flags">
									<Link href={`/guides/${guide.slug}`}>
										<Title
											order={2}
											style={{
												textTransform: 'capitalize',
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
												flexWrap: 'wrap',
											}}
										>
											{guide.companyName.toLowerCase()}
											{guide.isPremium && (
												<Badge
													variant="filled"
													style={{
														background:
															'linear-gradient(135deg, #FFD700, #B8860B)',
														color: '#000',
														padding: '6px 12px',
														borderRadius: '20px',
														boxShadow: '0 2px 6px rgba(184, 134, 11, 0.4)',
														border: '1px solid rgba(255, 255, 255, 0.4)',
													}}
												>
													<span
														style={{
															display: 'flex',
															alignItems: 'center',
															gap: '4px',
															fontWeight: 'bold',
														}}
													>
														<span>Recommended</span> <span>★</span>
													</span>
												</Badge>
											)}
										</Title>
									</Link>
									<div id="countries">
										{guide.countries.map(
											(isoCode: string, countryIndex: number) => (
												<CountryBadge
													key={'country' + countryIndex}
													isoCode={isoCode}
													isHighlighted={highlightedCountryISOCodes.includes(
														isoCode,
													)}
												/>
											),
										)}
									</div>
								</div>
							</div>

							{!!guide.jsonProfile.website && (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 4,
										marginTop: '8px',
									}}
								>
									<IconWorldWww strokeWidth={1} size="1rem" />{' '}
									<a
										href={`/api/link?pGuideCompanyIdProfileClicked=${guide.id}`}
										target="_blank"
										rel="noopener nofollow"
									>
										{guide.jsonProfile.website}
									</a>
								</div>
							)}
							<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
								<IconId strokeWidth={1} size="1rem" />
								<Link href={`/guides/${guide.slug}`}>
									View full profile + contact details
								</Link>
							</div>
							{guide.companyReviewData?.totalReviewAverage &&
								guide.companyReviewData.totalReviewCount && (
									<div
										style={{ display: 'flex', alignItems: 'center', gap: 4 }}
									>
										<IconStar strokeWidth={1} size="1rem" />
										<Link href={`/guides/${guide.slug}#reviews`}>
											Rated{' '}
											{(
												guide.companyReviewData.totalReviewAverage / 20
											).toFixed(1)}{' '}
											{pluralize(
												'star',
												guide.companyReviewData.totalReviewCount,
											)}{' '}
											({guide.companyReviewData.totalReviewCount}{' '}
											{pluralize(
												'review',
												guide.companyReviewData.totalReviewCount,
											)}
											)
										</Link>
									</div>
								)}

							{!!guide.jsonProfile.aboutInfoBlurb && (
								<Text fw={500} mt="md">
									{guide.jsonProfile.aboutInfoBlurb}
								</Text>
							)}
							{guide.guideTripsSummary.length === 0 &&
								guide.guideTripMultiPeaksSummary.length === 0 && (
									<Alert mt="md">
										We don&apos;t have any trips listed for this guide yet.
									</Alert>
								)}
							{guide.guideTripsSummary.length > 0 && (
								<>
									<Title order={3} mt="sm" mb="sm">
										Single peak Expeditions
									</Title>
									<div className="trip-offering-prices">
										<Spoiler
											maxHeight={130}
											showLabel="Show more"
											hideLabel="Show less"
										>
											{guide.guideTripsSummary.map((trip, idxGuideTrip) => (
												<Link
													href={getTrekRoute(
														Types.App.DataTypes.TrekOrClimb.CLIMB,
														trip.continent,
														trip?.countries ?? [],
														sluggify(trip.name),
													)}
													key={'guideTrip' + idxGuideTrip}
												>
													<div
														// className="small-trip-offering-summary"
														// key={trip.id}
														className={classNames({
															'small-trip-offering-summary': true,
															highlighted: highlightedTripIds.includes(
																trip.tripId,
															),
														})}
													>
														{trip.uploadId !== undefined && (
															<Image
																src={getUploadThumbPath(trip.uploadId, 'small')}
																alt={`${guide.companyName} Guided expedition to ${trip.name}`}
																objectFit="cover"
																height={24}
																width={24}
																style={{
																	borderRadius: 4,
																}}
															/>
														)}
													<Text
														fw={500}
														style={{ display: 'inline-block' }}
														component="div"
													>
															{trip.name}
															<Badge variant="filled" ml="xs" className="white">
																${(+trip.price).toLocaleString()}
															</Badge>
														</Text>
													</div>
												</Link>
											))}
										</Spoiler>
									</div>
								</>
							)}
							{guide.guideTripMultiPeaksSummary.length > 0 && (
								<>
									<Title order={3} mt="sm" mb="sm">
										Multiple peak Expeditions
									</Title>
									<div className="trip-offering-prices">
										{guide.guideTripMultiPeaksSummary.map(
											(multiPeakTrip, idxMultiPeakTrip) => (
												<div
													className="small-trip-offering-summary"
													key={'multiTripPeak' + idxMultiPeakTrip}
													style={{ gap: 8, display: 'inline-flex' }}
												>
													<div>
														{multiPeakTrip.individualTrips?.map(
															(trip, idxMultiPeakTripTrip) => (
																<div
																	className="multipeak-trip-part"
																	key={
																		'multiPeakTripTrip' + idxMultiPeakTripTrip
																	}
																>
																	{!!trip?.uploadId && (
																		<Image
																			src={getUploadThumbPath(
																				trip.uploadId,
																				'small',
																			)}
																			alt={`${guide.companyName} Guided expedition to ${trip.name}`}
																			// layout="fill"
																			objectFit="cover"
																			// quality={100}
																			height={24}
																			width={24}
																			style={{
																				borderRadius: 4,
																			}}
																		/>
																	)}
																	<Text
																		fw={500}
																		style={{ display: 'inline-block' }}
																	>
																		{trip.name}
																	</Text>
																</div>
															),
														)}
													</div>
													<Badge
														variant="filled"
														ml="xs"
														style={{
															overflow: 'visible',
															textOverflow: 'initial',
														}}
														className="white"
													>
														${(+multiPeakTrip.price).toLocaleString()}
													</Badge>
												</div>
											),
										)}
									</div>
								</>
							)}
							{guide.certificates.length > 0 && (
								<>
									<Title order={3} mt="sm" mb="sm">
										Certificates
									</Title>
									<div
										style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
									>
										{guide.certificates.map((cert, idxCert) => (
											<Popover
												key={idxCert}
												position="bottom"
												withArrow
												shadow="md"
												arrowSize={16}
											>
												<Popover.Target>
													<div
														style={{
															border: '1px solid silver',
															borderRadius: '6px',
															padding: '2px 6px',
															display: 'flex',
															alignItems: 'center',
															gap: '8px',
															cursor: 'zoom-in',
														}}
													>
														<IconCertificate
															size={20}
															style={{
																verticalAlign: 'middle',
																lineHeight: '100%',
															}}
														/>
														<span>{cert.certificateName}</span>
													</div>
												</Popover.Target>
												<Popover.Dropdown>
													<MantineImage
														style={{
															maxWidth: '80vw',
															height: 'auto',
															maxHeight: '80vw',
														}}
														src={getUploadThumbPath(cert.imageId, 'medium')}
														radius="md"
													/>
												</Popover.Dropdown>
											</Popover>
										))}
									</div>
								</>
							)}
						</Paper>
					</div>
				))}
			</div>

			{totalPages > 1 && (
				<Center mt="xl" mb="xl">
					<Pagination
						value={currentPage}
						onChange={handlePageChange}
						total={totalPages}
						size="md"
						radius="md"
						withEdges
						// Collapse page numbers to a "Page X of Y" label on narrow
						// screens so the crawlable anchors never overflow.
						layout="responsive"
						// Shared pagination skin (app/style.scss): neutralises the global
						// a/:visited link colours on the crawlable <a> controls.
						classNames={{ root: 'gt-pagination' }}
						// Render every control as a real <a href> so crawlers can reach
						// all directory pages (and the guide profiles linked from them).
						getItemProps={(page) => pageAnchorProps(page)}
						getControlProps={(control) => {
							const targetPage =
								control === 'first'
									? 1
									: control === 'previous'
									? currentPage - 1
									: control === 'next'
									? currentPage + 1
									: totalPages
							if (targetPage < 1 || targetPage > totalPages) return {}
							return pageAnchorProps(targetPage)
						}}
					/>
				</Center>
			)}
		</Box>
	)
}

export default GuideDirectoryList
